import { Controller } from '@hotwired/stimulus';
import fitAutoresizeTextareas from 'helpers/autoresize';
import { fillRegion, nameSegments } from 'helpers/form-region';

// Goals v3 — the Discharge Goals v3 control (prototype's discharge-v2 page).
// Each open goal renders as an expandable disclosure row whose far-right
// select IS the discharge decision: keep the goal open, or mark it met /
// discontinued when the discharge signs. Picking a closing choice pops the
// row open so the required reason is right there; setting the select back
// to keep hides the reason band and (for an already-staged decision)
// promises the withdrawal the next save performs. Everything submits with
// the discharge's datum — this controller is presentation only, the
// DischargeReconciler is the truth at save time.
//
// The decision is an Impulse select: an <awc-autocomplete> whose blank value
// is keep, whose value is read-only (clear() empties it without announcing a
// change), and which announces a pick with awc-autocomplete:commit and its
// clear button with awc-autocomplete:clear.
//
// One note per goal, never two: the comment box belongs to a kept goal,
// and a closing decision's reason IS its comment (it is added to the goal
// when the discharge signs). Closing hides the comment box and stops it
// submitting, carrying anything typed there into the reason; going back to
// keep before that reason is edited puts the text back where it came from.
//
// The caret before the section's title (allToggle) opens or closes every
// shown goal at once, as the caret on a visit note's and a Plan of Care's
// goals does: any shown row closed, every one opens; otherwise every row
// closes. It reads as open while every shown row is.
//
// Targets:
//   allToggle - the caret before the title (optional)
export default class extends Controller {
  static targets = ['allToggle'];

  connect() {
    // A save-time violation must be seen: the server stamps the flagged
    // rows, and every flagged row starts open (the partial renders them
    // open); nothing to do here beyond initial reason-band sync.
    this.element.querySelectorAll('[data-goal-key]').forEach((row) => this._syncDecision(row, { focus: false }));
    this._syncAllToggle();
  }

  // Row header click (including the caret button's native activation).
  // The decision select and anything interactive never toggle the row.
  toggleRow(event) {
    if (event.target.closest('select, a, button:not(.gv3-row-disclosure), input, textarea, awc-autocomplete')) return;
    const row = event.currentTarget.closest('[data-goal-key]');
    if (!row) return;
    const detail = row.querySelector('.goals-v3-goal-collapsible');
    if (!detail) return;
    this._setRowOpen(row, detail.classList.contains('hidden'));
  }

  // The title's caret: open every shown goal while any is closed, else
  // close every one. A goal the document's Customize Goals Shown choice
  // hides is left as it is, so it does not reappear already open.
  toggleAllRows() {
    const rows = this._shownRows();
    if (rows.length === 0) return;
    const opening = rows.some((row) => {
      const detail = row.querySelector('.goals-v3-goal-collapsible');
      return detail && detail.classList.contains('hidden');
    });
    rows.forEach((row) => this._setRowOpen(row, opening));
  }

  _shownRows() {
    return Array.from(this.element.querySelectorAll('[data-goal-key]')).filter(
      (row) => !row.classList.contains('gv3-presentation-hidden') && !row.closest('[hidden]')
    );
  }

  // The caret reads as open while every shown goal is.
  _syncAllToggle() {
    if (!this.hasAllToggleTarget) return;
    const rows = this._shownRows();
    const open =
      rows.length > 0 &&
      rows.every((row) => {
        const detail = row.querySelector('.goals-v3-goal-collapsible');
        return detail && !detail.classList.contains('hidden');
      });
    this.allToggleTargets.forEach((button) => {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      const caret = button.querySelector('.gv3-expand-caret');
      if (caret) caret.classList.toggle('is-open', open);
    });
  }

  decisionChanged(event) {
    const row = event.currentTarget.closest('[data-goal-key]');
    if (!row) return;
    this._syncDecision(row, { focus: true });
  }

  // auto-save:restore hands back a copy of the decisions kept by auto-save,
  // the session-timeout save or the offline save: what the control would
  // have submitted then (helpers/form-region.js). Each goal in the copy takes
  // its decision, reason and comment back, and its row's bands follow the
  // decision; a goal the copy does not hold (one that opened since) keeps
  // what it has.
  restore(event) {
    const fields = (event.detail && event.detail.fields) || [];
    const prefix = this.element.dataset.autoSaveRegion;
    const copied = new Set(fields.map(([name]) => nameSegments(name, prefix)[0]).filter(Boolean));
    const rows = Array.from(this.element.querySelectorAll('[data-goal-key]')).filter((row) =>
      copied.has(row.dataset.goalKey)
    );

    rows.forEach((row) => {
      // A comment the page carried into the reason belongs to the page the
      // copy replaces, and a closing decision's comment box submits nothing,
      // so the copy holds no comment for it.
      delete row.dataset.gv3CarriedComment;
      delete row.dataset.gv3ReasonBeforeCarry;
      delete row.dataset.gv3ReasonAfterCarry;
      const comment = row.querySelector('[data-goals-v3-discharge-comment]');
      if (comment) comment.value = '';
    });
    fillRegion(this.element, fields, prefix);
    rows.forEach((row) => this._syncDecision(row, { focus: false }));
  }

  // Withdraw a staged close: back to keep. The draft itself is withdrawn by
  // the reconciler on the next save — the chip hides now as the promise.
  withdraw(event) {
    event.preventDefault();
    const row = event.currentTarget.closest('[data-goal-key]');
    if (!row) return;
    const select = row.querySelector('[data-goals-v3-discharge-decision]');
    if (select && select.value !== '') select.clear();
    this._syncDecision(row, { focus: false });
  }

  _syncDecision(row, { focus }) {
    const select = row.querySelector('[data-goals-v3-discharge-decision]');
    if (!select) return;
    const closing = select.value === 'met' || select.value === 'discontinued';

    const reasonBand = row.querySelector('[data-goals-v3-discharge-target~="reasonBand"]');
    const keepNote = row.querySelector('[data-goals-v3-discharge-target~="keepNote"]');
    const chip = row.querySelector('[data-goals-v3-discharge-target~="stagedChip"]');
    const reason = row.querySelector('[data-goals-v3-discharge-reason]');
    const commentBand = row.querySelector('[data-goals-v3-discharge-target~="commentBand"]');
    const comment = row.querySelector('[data-goals-v3-discharge-comment]');

    if (reasonBand) reasonBand.hidden = !closing;
    if (keepNote) keepNote.hidden = closing;
    this._syncComment(row, { closing, reason, commentBand, comment });
    // The chip narrates the PERSISTED draft; flipping back to keep hides it
    // (withdraw-on-save), and re-picking the staged status brings it back.
    if (chip) chip.hidden = !closing || chip.dataset.stagedStatus !== select.value;
    if (reason && closing) {
      reason.placeholder =
        select.value === 'met' ? 'How the goal was met (required)…' : 'Why the goal is being discontinued (required)…';
      if (focus) {
        this._setRowOpen(row, true);
        this._focusAfterPick(reason);
      }
    }
  }

  // The autocomplete announces a pick from inside the option's click (the Enter key clicks the
  // active option too), and that same click then reaches the autocomplete's own handler, which
  // focuses its input. So the reason takes the focus once the click is done.
  _focusAfterPick(reason) {
    setTimeout(() => {
      if (reason.isConnected) reason.focus();
    }, 0);
  }

  // The comment box shows (and submits) only while the goal is kept open.
  // Closing carries its text into the reason; returning to keep with the
  // carried reason untouched restores both boxes to what they held.
  _syncComment(row, { closing, reason, commentBand, comment }) {
    if (!comment) return;
    const carry = row.dataset;
    if (closing) {
      const typed = comment.value.trim();
      if (typed && reason) {
        carry.gv3ReasonBeforeCarry = reason.value;
        carry.gv3CarriedComment = comment.value;
        reason.value = reason.value.trim() ? `${reason.value.trim()}\n${typed}` : typed;
        carry.gv3ReasonAfterCarry = reason.value;
        comment.value = '';
      }
    } else if (reason && carry.gv3CarriedComment !== undefined && reason.value === carry.gv3ReasonAfterCarry) {
      reason.value = carry.gv3ReasonBeforeCarry || '';
      comment.value = carry.gv3CarriedComment;
    }
    if (!closing) {
      delete carry.gv3CarriedComment;
      delete carry.gv3ReasonBeforeCarry;
      delete carry.gv3ReasonAfterCarry;
    }
    if (commentBand) commentBand.hidden = closing;
    comment.disabled = closing;
    // The box on show takes the size of what it holds: the page sized it
    // while it was out of sight, and a carried comment may have come back.
    if (commentBand && !closing) fitAutoresizeTextareas(commentBand);
  }

  // An opened row sizes its comment box: the page sized it as it loaded, and
  // the rows start closed.
  _setRowOpen(row, open) {
    const detail = row.querySelector('.goals-v3-goal-collapsible');
    if (detail) detail.classList.toggle('hidden', !open);
    const header = row.querySelector('.gv3-expand-row');
    if (header) header.classList.toggle('is-open', open);
    const disclosure = row.querySelector('.gv3-row-disclosure');
    if (disclosure) disclosure.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) fitAutoresizeTextareas(row);
    this._syncAllToggle();
  }
}
